import React, { useState, useEffect, useCallback } from 'react';
import { Box, IconButton, Container, Typography, useTheme, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AnimatedSection from './AnimatedSection';

const images = [
  '/offers/1500 MEN_AND_WOMEN_03.jpg.jpeg',
  '/offers/1500..jpg.jpeg',
  '/offers/2999-3.jpg.jpeg',
  '/offers/3499-2.jpg.jpeg',
  '/offers/3499-3.jpg.jpeg',
  '/offers/6k-2.jpg.jpeg',
  '/offers/Female_6000x02.jpg.jpeg',
  '/offers/HYDRA_02.jpg.jpeg',
  '/offers/Rs_5999_02.jpg.jpeg',
];

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
    scale: 0.9,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
    scale: 1,
  },
  exit: (direction) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
    scale: 0.9,
  }),
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

export default function OffersSlider() {
  const [[page, direction], setPage] = useState([0, 0]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const imageIndex = Math.abs(page % images.length);

  const paginate = useCallback((newDirection) => {
    setPage([page + newDirection, newDirection]);
  }, [page]);

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 5000);
    return () => clearInterval(timer);
  }, [paginate]);

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#fff' }}>
      <Container maxWidth="lg">
        <AnimatedSection>
          <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
            <Typography
              sx={{
                fontFamily: '"Lato", sans-serif',
                fontSize: '0.75rem',
                color: '#FF0099',
                letterSpacing: '0.25em',
                textTransform: 'uppercase',
                mb: 1,
              }}
            >
              SPECIAL OFFERS
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: '#1a1a1a',
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                fontFamily: '"Playfair Display", serif',
                fontWeight: 700,
              }}
            >
              Exclusive Deals for You
            </Typography>
            <Box
              sx={{
                width: 60,
                height: 3,
                backgroundColor: '#FF0099',
                mx: 'auto',
                mt: 2,
                borderRadius: 2,
              }}
            />
          </Box>
        </AnimatedSection>

        <Box
          sx={{
            position: 'relative',
            height: { xs: '350px', sm: '500px', md: '600px' },
            width: '100%',
            overflow: 'hidden',
            borderRadius: { xs: 4, md: 6 },
            boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
            backgroundColor: '#f5f5f5',
          }}
        >
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={page}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: 'spring', stiffness: 300, damping: 30 },
                opacity: { duration: 0.3 },
                scale: { duration: 0.4 },
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(e, { offset, velocity }) => {
                const swipe = swipePower(offset.x, velocity.x);

                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                cursor: 'grab',
              }}
            >
              <Box
                component="img"
                src={images[imageIndex]}
                alt={`Offer ${imageIndex + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain', // Changed to contain to show full posters
                  backgroundColor: '#000', // Black background for contain
                }}
              />
            </motion.div>
          </AnimatePresence>

          {/* Navigation Arrows */}
          {!isMobile && (
            <>
              <IconButton
                onClick={() => paginate(-1)}
                sx={{
                  position: 'absolute',
                  left: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  color: '#FF0099',
                  '&:hover': {
                    backgroundColor: '#FF0099',
                    color: '#fff',
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <ArrowBackIosNewIcon fontSize="small" />
              </IconButton>

              <IconButton
                onClick={() => paginate(1)}
                sx={{
                  position: 'absolute',
                  right: 20,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  color: '#FF0099',
                  '&:hover': {
                    backgroundColor: '#FF0099',
                    color: '#fff',
                  },
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                }}
              >
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
            </>
          )}

          {/* Indicators */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 25,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              display: 'flex',
              gap: 1.5,
              backgroundColor: 'rgba(0,0,0,0.3)',
              px: 2,
              py: 1,
              borderRadius: 5,
              backdropFilter: 'blur(4px)',
            }}
          >
            {images.map((_, index) => (
              <Box
                key={index}
                onClick={() => {
                  const diff = index - imageIndex;
                  if (diff !== 0) paginate(diff);
                }}
                sx={{
                  width: index === imageIndex ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: index === imageIndex ? '#FF0099' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: index === imageIndex ? '#FF0099' : 'rgba(255,255,255,0.8)',
                  },
                }}
              />
            ))}
          </Box>
        </Box>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography
            sx={{
              fontFamily: '"Lato", sans-serif',
              fontSize: '0.9rem',
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            * Terms and conditions apply. Offers valid for a limited time.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
